import {
  ChevronRightIcon,
  EnvelopeIcon,
  FolderIcon,
  HomeIcon,
  IdentificationIcon,
  PhoneIcon,
  ShieldCheckIcon,
  UserIcon
} from '@heroicons/react/24/solid'
import packageInfo from '@root/package.json'
import { FC } from 'react'

import { useAuth } from '@/api/models/auth/hooks/useAuth'
import { usePermissions } from '@/api/models/auth/hooks/usePermissions'
import { useContactInfo } from '@/api/models/common/hooks/useContactInfo'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { cn } from '@/app/utils'
import { AppButton } from '@/components/shared/button'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { formatPhoneNumber, makePhoneCallable } from '@/utils/number'

import ExitLogoutSvg from '../assets/ExitLogout.svg'
import InfoIcon from '../assets/InfoIcon.svg'
import { SetUserProfileRoute } from '../hooks/useUserProfileRoute'
import { UserProfileScreens } from '../lib/types'
import { getDisplayUserName } from '../lib/utils'
import UserProfileInfo from './UserProfileInfo'

type Props = {
  setUserProfileRoute: SetUserProfileRoute
  contentHeight: string
  setFooterRef: (ref: HTMLElement | null) => void
}

const UserProfileScreen: FC<Props> = ({
  setUserProfileRoute,
  contentHeight,
  setFooterRef
}) => {
  const { user, logout } = useAuth()
  const {
    data: contactInfo,
    isLoading: contactInfoLoading,
    hasContactInfo
  } = useContactInfo()
  const { reachGoal } = useYandexMetrika()
  const permissions = usePermissions()

  const version = packageInfo.version

  const infoItems = [
    {
      key: 'name',
      icon: <UserIcon />,
      value: getDisplayUserName(user)
    },
    {
      key: 'phone',
      icon: <PhoneIcon />,
      value: formatPhoneNumber(user?.phone_number)
    },
    {
      key: 'email',
      icon: <EnvelopeIcon />,
      value: user?.email
    }
  ]

  const screenItems = [
    {
      screen: UserProfileScreens.Accesses,
      Icon: ShieldCheckIcon,
      label: 'Доступы',
      goal: YandexMetrikaEvent.MAIN_ACCESS
    },
    {
      screen: UserProfileScreens.Projects,
      Icon: HomeIcon,
      label: 'Проекты',
      goal: YandexMetrikaEvent.MAIN_PROJECTS
    },
    ...(permissions.requests
      ? [
          {
            screen: UserProfileScreens.Autos,
            Icon: IdentificationIcon,
            label: 'Мой транспорт',
            goal: YandexMetrikaEvent.MY_AUTO_LIST
          }
        ]
      : []),
    {
      screen: UserProfileScreens.Documents,
      Icon: FolderIcon,
      label: 'Документы'
    }
  ]

  const onButtonClick = (
    screen: UserProfileScreens,
    goal?: YandexMetrikaEvent
  ) => {
    setUserProfileRoute(screen)
    if (goal) {
      reachGoal(goal)
    }
  }

  return (
    <>
      <div
        className="overflow-y-auto px-40 lg:px-24 sm:px-16"
        style={{ height: contentHeight }}
      >
        <UserProfileInfo infoItems={infoItems} className="mt-16 lg:mt-0" />

        {hasContactInfo && (
          <div className="mt-20 flex space-x-8 rounded-12 bg-white p-16">
            <div className="w-20 pt-3">
              <InfoIcon />
            </div>

            <div className="font-pro text-grey-70 text-16">
              Чтобы изменить личные данные, позвоните в контактный центр{' '}
              {contactInfo?.contactPhone && (
                <a
                  href={`tel:${makePhoneCallable(contactInfo.contactPhone)}`}
                  className="whitespace-nowrap font-bold text-bronze-40 transition-colors hover:text-bronze-60"
                >
                  {contactInfoLoading
                    ? '(Загружается...)'
                    : formatPhoneNumber(contactInfo.contactPhone)}
                </a>
              )}
              {contactInfo?.contactPhone && contactInfo?.contactEmail && ' '}
              {contactInfo?.contactEmail && (
                <>
                  или отправьте обращение на почту{' '}
                  <a
                    href={`mailto:${contactInfo.contactEmail}`}
                    className="font-bold text-bronze-40 transition-colors hover:text-bronze-60"
                  >
                    {contactInfoLoading
                      ? '(Загружается...)'
                      : contactInfo.contactEmail}
                  </a>
                </>
              )}
            </div>
          </div>
        )}

        <div className="mt-40 flex flex-col gap-40 lg:mt-20 lg:gap-24">
          {screenItems.map(({ screen, Icon, label, goal }) => (
            <AppButton
              key={screen}
              fluid
              variant="uncolored"
              className={cn(
                'flex !h-auto items-center justify-between !px-0 !py-12 focus:outline-none',
                '[&:not(:first-child)]:relative [&:not(:first-child)]:before:absolute',
                '[&:not(:first-child)]:before:-top-20 [&:not(:first-child)]:before:left-0',
                '[&:not(:first-child)]:before:h-1 [&:not(:first-child)]:before:w-full',
                '[&:not(:first-child)]:before:bg-grey-30 lg:[&:not(:first-child)]:before:-top-12'
              )}
              onClick={() => onButtonClick(screen, goal)}
            >
              <div className="flex items-center gap-8">
                <Icon className="h-28 w-28 lg:h-24 lg:w-24" />
                <span className="text-24 lg:text-20 sm:text-18">{label}</span>
              </div>
              <ChevronRightIcon className="h-28 w-28" />
            </AppButton>
          ))}
        </div>
      </div>

      <div ref={setFooterRef}>
        <div className="border-t border-grey-20 p-40 pt-20 lg:px-24 sm:px-16 sm:pb-20">
          <AppButton
            variant="backless"
            size="large"
            className="w-full sm:!h-44"
            onClick={logout}
          >
            <ExitLogoutSvg />
            <span className="ml-8">Выйти</span>
          </AppButton>

          <div className="mt-10 flex flex-col items-center gap-4 text-16">
            <p>Версия {version}</p>
          </div>
        </div>
      </div>
    </>
  )
}

export default UserProfileScreen
